import { request } from './client';

/** Who reads listing titles: Jev, Jev with our models watching, or our own models. */
export type ReaderMode = 'jev' | 'watch' | 'own';

export interface ReaderConfig {
  mode: ReaderMode;
  /** Who picks the exact catalogue product: same three choices. */
  pick: ReaderMode;
  models: string[];
  sure: number;
  source: 'env' | 'observe';
  updatedAt: string | null;
  updatedBy: string | null;
  note: string | null;
}

export interface ReaderConfigResponse {
  config: ReaderConfig;
  env: ReaderConfig;
  modes: ReaderMode[];
  /** `loaded`: what the service has loaded right now (its /health), null when it could not be asked. */
  titleService: { configured: boolean; reachable: boolean; loaded?: { models: string[]; picker: string | null } | null };
}

export interface ReadSide {
  category: string | null;
  product_type: string | null;
  single_model: boolean | null;
  brand: string | null;
  confidence: number | null;
}

export type CompareField = 'category' | 'product_type' | 'single_model' | 'brand';

export interface ReaderCompareRow {
  identity_ref: string;
  deal_id: string | null;
  source: string;
  title: string;
  ts: string;
  mode: string;
  decided_by: 'jev' | 'own' | 'backup';
  why: string | null;
  jev: ReadSide | null;
  jev_error: string | null;
  own: (ReadSide & { agree: boolean; unsettled: string[]; models: string[]; by: string[] }) | null;
  same: Record<CompareField, boolean> | null;
}

export interface ReaderSummary {
  hours: number;
  reads: number;
  decidedBy: Record<string, number>;
  bothRead: number;
  same: Record<CompareField, { same: number; of: number }>;
  allSame: number;
  ownRead: number;
  ownSettled: number;
  unsettledTop: Array<{ reason: string; n: number }>;
  jevErrors: Record<string, number>;
}

export async function fetchReaderConfig(): Promise<ReaderConfigResponse> {
  const r = await request<{ ok: true } & ReaderConfigResponse>('/v1/observe/reader/config');
  return { config: r.config, env: r.env, modes: r.modes, titleService: r.titleService };
}

export async function saveReaderConfig(body: { mode?: ReaderMode; pick?: ReaderMode; models?: string[]; sure?: number; note?: string; reset?: boolean }): Promise<ReaderConfig> {
  return (await request<{ ok: true; config: ReaderConfig }>('/v1/observe/reader/config', { method: 'POST', body })).config;
}

export async function fetchReaderSummary(hours: number): Promise<ReaderSummary> {
  return (await request<{ ok: true; summary: ReaderSummary }>(`/v1/observe/reader/summary?hours=${hours}`)).summary;
}

export async function fetchReaderRecent(limit: number): Promise<ReaderCompareRow[]> {
  return (await request<{ ok: true; rows: ReaderCompareRow[] }>(`/v1/observe/reader/recent?limit=${limit}`)).rows;
}

export interface PickSide {
  modelKey: string | null;
  reason: string;
  p: number;
}

export interface PickCompareRow {
  deal_id: string | null;
  source: string;
  title: string;
  category: string;
  brand: string | null;
  ts: string;
  mode: string;
  decided_by: 'jev' | 'own';
  why: string | null;
  candidates: number;
  jev: PickSide | null;
  jev_error: string | null;
  own: (PickSide & { by: string }) | null;
  same: boolean | null;
}

export interface PickSummary {
  hours: number;
  picks: number;
  decidedBy: Record<string, number>;
  both: number;
  samePicked: number;
  sameNone: number;
  different: number;
  onlyJevPicked: number;
  onlyOwnPicked: number;
  jevErrors: Record<string, number>;
}

export async function fetchPickSummary(hours: number): Promise<PickSummary> {
  return (await request<{ ok: true; summary: PickSummary }>(`/v1/observe/reader/pick/summary?hours=${hours}`)).summary;
}

export async function fetchPickRecent(limit: number): Promise<PickCompareRow[]> {
  return (await request<{ ok: true; rows: PickCompareRow[] }>(`/v1/observe/reader/pick/recent?limit=${limit}`)).rows;
}
